const FULLY_TRANSPARENT_ALPHA = 0;
const ALPHA_CHANNEL_OFFSET = 3;
const CHANNELS_PER_PIXEL = 4;
const SVG_MIME_TYPE = "image/svg+xml";
const VIEW_BOX_SEPARATOR = /[\s,]+/;
const SAMPLE_LONG_EDGE = 128;
const DEFAULT_OBJECT_SIZE = { width: 300, height: 150 };

type Size = { width: number; height: number };

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not decode image: ${src}`));
    image.src = src;
  });

/**
 * Sampling at any other ratio letterboxes the artwork, and the transparent bars
 * `preserveAspectRatio` pads with would pass the check on their own.
 */
const getSvgSize = (markup: string): Size => {
  const viewBox = new DOMParser()
    .parseFromString(markup, SVG_MIME_TYPE)
    .documentElement.getAttribute("viewBox");

  const [, , viewBoxWidth = NaN, viewBoxHeight = NaN] =
    viewBox?.trim().split(VIEW_BOX_SEPARATOR).map(Number) ?? [];

  if (!(viewBoxWidth > 0) || !(viewBoxHeight > 0)) return DEFAULT_OBJECT_SIZE;

  return { width: viewBoxWidth, height: viewBoxHeight };
};

/**
 * An SVG carrying only a `viewBox` — SVGO's default output — has no intrinsic
 * size, so it has to be given one before it can be sampled.
 */
const getSourceSize = async (
  image: HTMLImageElement,
  file: File,
): Promise<Size | null> => {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (width && height) return { width, height };

  return file.type === SVG_MIME_TYPE ? getSvgSize(await file.text()) : null;
};

const scaleToSample = ({ width, height }: Size): Size => {
  const scale = Math.min(1, SAMPLE_LONG_EDGE / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * A removed background leaves transparent pixels; a plate behind the artwork
 * leaves none. One transparent pixel anywhere is enough — artwork can run off
 * an edge, fill a corner or be hollow in the middle and still be a cut-out — so
 * an opaque image with rounded corners passes too.
 *
 * Fails open on anything we cannot inspect: an undecodable image, or a canvas
 * tainted by an SVG pulling in a cross-origin asset.
 */
export const hasTransparentBackground = async (
  file: File,
): Promise<boolean> => {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const sourceSize = await getSourceSize(image, file);

    if (!sourceSize) return true;

    const sample = scaleToSample(sourceSize);

    const canvas = document.createElement("canvas");
    canvas.width = sample.width;
    canvas.height = sample.height;

    const context = canvas.getContext("2d");

    if (!context) return true;

    // A same-size canvas for an 8000x8000 upload costs ~256 MB and can exceed
    // the browser's area cap, past which `drawImage` silently no-ops — leaving
    // an all-transparent buffer that passes every image. Alpha blends on the
    // way down, but no background is thinner than the scale factor.
    context.drawImage(image, 0, 0, sample.width, sample.height);

    const { data } = context.getImageData(0, 0, sample.width, sample.height);

    return data.some(
      (channel, index) =>
        index % CHANNELS_PER_PIXEL === ALPHA_CHANNEL_OFFSET &&
        channel === FULLY_TRANSPARENT_ALPHA,
    );
  } catch {
    return true;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
